import React, { useState, useEffect } from 'react';
import { http, getAuthorizationHeader } from '../../config/http';
import Layouts from '../../layouts/Layouts';
import {
  IconCheck,
  IconX,
  IconSearch,
  IconCalendarTime,
  IconReload
} from '@tabler/icons-react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; 
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';
import { toast } from 'react-toastify';
import { DinamicTitle } from '../../utils/DinamicTitle';

const VerifikasiHapusPembelian = () => {
  DinamicTitle('Verifikasi Hapus Pembelian');
  const token = localStorage.getItem('token');

  const [pembelians, setPembelians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Date Filter State
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const selectionRange = {
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: 'selection',
  };

  const handleSelectDate = (date) => {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
    setPage(1);
  };

  const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
  const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

  const fetchPendingDeletions = async () => {
    setLoading(true);
    try {
      const url = `pembelian/deletions/pending?page=${page}${search ? `&kode=${search}` : ''}${awal && akhir ? `&tgl_awal=${awal}&tgl_akhir=${akhir}` : ''}`;
      const res = await http.get(url, {
        headers: { Authorization: getAuthorizationHeader(token) }
      });
      setPembelians(res.data.data.data);
      setLastPage(res.data.data.last_page);
    } catch (error) {
      console.error(error);
      toast.error('Gagal mengambil data hapus pembelian');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPendingDeletions();
  }, [page, search, startDate, endDate]);

  const handleApprove = async (id) => {
    try {
      const res = await http.post(`pembelian/deletions/${id}/approve`, {}, {
        headers: { Authorization: getAuthorizationHeader(token) }
      });
      toast.success(res.data.message);
      fetchPendingDeletions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menyetujui');
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await http.post(`pembelian/deletions/${id}/reject`, {}, {
        headers: { Authorization: getAuthorizationHeader(token) }
      });
      toast.success(res.data.message);
      fetchPendingDeletions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menolak');
    }
  };

  return (
    <Layouts title="Verifikasi Hapus Pembelian"
      compFilter={
        <div className="flex flex-wrap items-center gap-2 justify-end w-full sm:w-auto">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="dropdown dropdown-bottom dropdown-end w-full sm:w-auto">
              <label tabIndex={0} className="btn bg-amber-500 hover:bg-amber-600 text-white w-full sm:w-auto border-none flex items-center justify-center gap-2 h-10 min-h-0">
                <IconCalendarTime size={18} />
                <span className="text-sm font-medium hidden sm:inline">Filter Tanggal</span>
              </label>
              <div tabIndex={0} className="dropdown-content z-50 p-3 shadow-xl bg-white dark:bg-slate-800 rounded-box w-max border border-slate-200 dark:border-slate-700 mt-2">
                <DateRange
                  ranges={[selectionRange]}
                  onChange={handleSelectDate}
                  className="rounded-lg overflow-hidden border border-slate-100 dark:border-slate-700/50 shadow-sm custom-date-picker"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => {
                      setStartDate(null);
                      setEndDate(null);
                    }}
                    className="w-full mt-3 btn btn-sm bg-red-50 text-red-600 hover:bg-red-100 border-none dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                  >
                    Reset Filter Tanggal
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={fetchPendingDeletions}
              className="btn bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 border-none w-10 sm:w-auto h-10 min-h-0 px-0 sm:px-4"
              title="Refresh Data"
            >
              <IconReload size={18} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 mt-4">
        <div className="relative w-full sm:max-w-xs">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IconSearch size={18} className="text-slate-400" />
          </div>
          <input
            type="text"
            className="input input-bordered w-full pl-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all h-10"
            placeholder="Cari faktur..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="block min-w-full shadow-sm sm:rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
        <div className="relative overflow-x-auto scrollbar-thin">
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/30">
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">No</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Faktur</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Barang</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Harga Beli</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Alasan Hapus</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Pengaju</th>
                <th className="py-3 px-4 text-left text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-center text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <div className="w-8 h-8 rounded-full animate-spin border-4 border-dashed border-amber-500 border-t-transparent"></div>
                      <span className="text-sm font-medium">Memuat data...</span>
                    </div>
                  </td>
                </tr>
              ) : pembelians.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    Tidak ada riwayat pengajuan hapus pembelian.
                  </td>
                </tr>
              ) : (
                pembelians.map((trx, idx) => (
                  <tr key={trx.id} className="hover:bg-amber-50 dark:hover:bg-slate-800/80 bg-white dark:bg-slate-900 transition-colors border-b border-slate-100 dark:border-slate-700/50">
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-300">{(page - 1) * 10 + idx + 1}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{trx.kode_trx}</span>
                      <div className="text-xs text-slate-500 mt-1">{trx.created_at}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-300">
                      <div>{trx.jenis_kategori?.nama_jenis || 'Barang'}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{trx.berat}g | {trx.karat}k</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-amber-600 dark:text-amber-500">
                      Rp {trx.harga_beli?.toLocaleString('id-ID')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-300">
                      {trx.alasan_hapus || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-300">
                      {trx.pengaju_hapus?.name || 'User ID: ' + trx.pengajuan_hapus_by}
                      <div className="text-xs text-slate-500">{trx.pengaju_hapus?.cabang?.lokasi || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-300">
                      {trx.status_pengajuan_hapus === 'pending' ? (
                        <span className="text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-1 rounded-full font-medium whitespace-nowrap">Pending</span>
                      ) : (
                        <span className="text-[11px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-1 rounded-full font-medium whitespace-nowrap">Ditolak</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-center gap-2">
                        {trx.status_pengajuan_hapus === 'pending' ? (
                          <>
                            <button
                              onClick={() => handleApprove(trx.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors text-xs font-medium"
                            >
                              <IconCheck size={16} /> Setujui
                            </button>
                            <button
                              onClick={() => handleReject(trx.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors text-xs font-medium"
                            >
                              <IconX size={16} /> Tolak
                            </button>
                          </>
                        ) : (
                          <span className="text-slate-400 text-xs italic">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Halaman <span className="font-medium text-slate-900 dark:text-slate-100">{page}</span> dari <span className="font-medium text-slate-900 dark:text-slate-100">{lastPage}</span>
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Kembali
              </button>
              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page === lastPage}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Selanjutnya
              </button>
            </div>
          </div>
        )}
      </div>
    </Layouts>
  );
};

export default VerifikasiHapusPembelian;
